import {
  Button,
  Center,
  Text,
  VStack,
  HStack,
  Stack,
  Box,
  Textarea,
  Modal,
  ModalBody,
  ModalFooter,
  IconButton,
  Input,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  Spinner,
  useToast,
  Stat,
  StatLabel,
  StatNumber,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  SimpleGrid,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  useDisclosure,
  TableCaption,
  TableContainer,
  Checkbox,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberDecrementStepper,
  NumberIncrementStepper,
  Select,
  Heading,
  Divider,
} from '@chakra-ui/react';
import {
  updateShingleOption,
  updateFinanceOption,
  updateProfitMargin,
  updateEstimateDescription,
  updateEstimateAmount,
  updateEstimateTitle,
  updateEstimateDescriptionBetter,
  updateEstimateAmountBetter,
  updateEstimateTitleBetter,
  updateEstimateDescriptionBest,
  updateEstimateAmountBest,
  updateEstimateTitleBest,
  updateEstimateProjectDescription,
  updateUpgradeOneDescription,
  updateUpgradeOnePrice,
  updateUpgradeTwoDescription,
  updateUpgradeTwoPrice,
  updateUpgradeThreeDescription,
  updateUpgradeThreePrice,
  updateEstimateLastSentDate,
  updateEstimateSigningTokenExpires,
  updateEstimateEmailStatus,
  updateEstimateAccessedDate,
  updateEstimateViewedDate,
  updateEstimateRejectedDate,
  updateEstimateRejectedReason,
  updateEstimateSigned,
  updateEstimateSignedDate,
} from '../features/calculator/calculatorSlice';
import { AddIcon, DeleteIcon, RepeatIcon } from '@chakra-ui/icons';
import { updateGeneralPayment } from '../features/labor/paymentSlice';
import axios from 'axios';
import useUser from '../hooks/useUser';
import { FaDownload, FaPlus } from 'react-icons/fa';
import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Papa from 'papaparse';
import { debounce } from 'lodash';
import { useMediaQuery } from '@chakra-ui/react';
import mixpanel from 'mixpanel-browser';
import { Document, Page, pdfjs } from 'react-pdf';
import EstimatePhotos, { PHOTOS_PER_PAGE } from './estimate/EstimatePhotos';
import EstimateCoverPhoto from './estimate/EstimateCoverPhoto';
import EstimateAttachments from './estimate/EstimateAttachments';
import CustomPricingOptions from './estimate/CustomPricingOptions';
import EmailPreview from './estimate/EmailPreview';
import { estimateApi } from './estimate/estimateApi';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//   'pdfjs-dist/build/pdf.worker.min.mjs',
//   import.meta.url
// ).toString();

// pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Hidden while we check whether anyone still uses it (Mixpanel: "Send to
// admin"). Set to true to bring the button back.
const SHOW_SEND_TO_ADMIN = false;

// Opening the Calculate modal recalculates if the last calculation is older
// than this, in case prices or labor settings changed elsewhere.
const RECALCULATE_ON_OPEN_AFTER_MS = 5 * 60 * 1000;

const SectionCard = ({ title, description, children }) => (
  <Box
    w="100%"
    bg="white"
    borderWidth="1px"
    borderColor="gray.200"
    borderRadius="lg"
    p={5}
  >
    <Box mb={4}>
      <Heading size="sm">{title}</Heading>
      {description && (
        <Text fontSize="xs" color="gray.500" mt={1}>
          {description}
        </Text>
      )}
    </Box>
    <VStack align="stretch" spacing={4}>
      {children}
    </VStack>
  </Box>
);

// Shown under a tier's description when it no longer matches the text set in
// Material > Shingles (the estimate keeps its own copy once saved).
const MaterialDescriptionHint = ({
  value,
  materialValue,
  onUse,
  isDisabled,
}) =>
  materialValue?.trim() && value !== materialValue ? (
    <HStack justify="space-between" mt={1} spacing={3}>
      <Text fontSize="xs" color="orange.600">
        Edited
      </Text>
      <Button
        size="xs"
        variant="link"
        colorScheme="blue"
        onClick={onUse}
        isDisabled={isDisabled}
      >
        Use default
      </Button>
    </HStack>
  ) : null;

const SignedBadge = ({ dateValue }) => (
  <HStack spacing={1}>
    <Text color="green.500">✅</Text>
    <Text fontSize="sm" fontWeight="semibold" color="green.600">
      Signed{' '}
      {new Date(dateValue).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })}{' '}
      at{' '}
      {new Date(dateValue).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })}
    </Text>
  </HStack>
);

const TIER_LABELS = { standard: 'Good', better: 'Better', best: 'Best' };

// Shared by Crew Bill and Material List, whose data is always [header, ...rows].
const DataTable = ({ data, size = 'sm' }) => (
  <Table variant="striped" size={size}>
    <Thead>
      <Tr>
        {data[0]?.map((header, index) => (
          <Th key={index}>
            <Text fontSize="xs">{header}</Text>
          </Th>
        ))}
      </Tr>
    </Thead>
    <Tbody>
      {data.slice(1)?.map((row, rowIndex) => (
        <Tr key={rowIndex}>
          {row?.map((cell, cellIndex) => (
            <Td key={cellIndex}>
              <Text fontSize="xs">{cell}</Text>
            </Td>
          ))}
        </Tr>
      ))}
    </Tbody>
  </Table>
);

const Calculate = ({
  isOpen,
  onClose,
  setTotalPrice,
  totalPrice,
  setMargin,
  setGrossProfit,
  setMarginPercent,
  setOverhead,
  setNetProfit,
  setAfterTaxNetProfit,
  setSalesCommission,
  setAfterTaxSalesCommission,
  setCompanyNet,
  setAfterTaxCompanyNet,
  setGrossProfitPercent,
  setOverheadPercent,
  setNetProfitPercent,
  setAfterTaxNetProfitPercent,
  setAfterTaxSalesCommissionPercent,
  setSalesCommissionPercent,
  setCompanyNetPercent,
  setAfterTaxCompanyNetPercent,
  calculateButtonClicked,
  setCalculateButtonClicked,
  setIsCalculateLoading,
  setPricePerMonth,
  setPricePerSq,
  setAvailableDiscount,
  setIfRedeckRequired,
  ifRedeckRequired,
  setAvailableDollarDiscount,
}) => {
  const [libraryItems, setLibraryItems] = useState([]);
  // Photos, PDFs and custom options for the current estimate. Each saves
  // as it changes.
  const [photoState, setPhotoState] = useState({
    photos: [],
    order: [],
    coverKey: '',
  });
  const [estimateAttachments, setEstimateAttachments] = useState([]);
  const [photoPosition, setPhotoPosition] = useState({
    placement: 'end',
    index: 0,
  });
  const [customOptions, setCustomOptions] = useState([]);
  const [estimatePdfPageCount, setEstimatePdfPageCount] = useState(0);
  const [downloadError, setDownloadError] = useState(false);
  const [breakdownError, setBreakdownError] = useState(false);
  const [crewBillError, setCrewBillError] = useState(false);
  const [materialListError, setMaterialListError] = useState(false);
  const [calculateLoading, setCalculateLoading] = useState(false);
  const financeOption = useSelector(state => state.calculator.financeOption);
  const cancelCalculateRef = React.useRef();
  const [crewBillLoading, setCrewBillLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [allLoading, setAllLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [estimatePdfLoading, setEstimatePdfLoading] = useState(false);
  const [signedPdfLoading, setSignedPdfLoading] = useState(false);
  const [sendPdfLoading, setSendPdfLoading] = useState(false);
  const [customItemDescription, setCustomItemDescription] = useState('');
  const [customItemQuantity, setCustomItemQuantity] = useState(0);
  const [customItemRate, setCustomItemRate] = useState(0);
  const [getItemizedPdfLoading, setGetItemizedPdfLoading] = useState(false);
  const [getEstimatePdfLoading, setGetEstimatePdfLoading] = useState(false);
  const [getSignedPdfLoading, setGetSignedPdfLoading] = useState(false);
  const [materialListLoading, setMaterialListLoading] = useState(false);
  const [itemizedListLoading, setItemizedListLoading] = useState(false);
  const [crewBillData, setCrewBillData] = useState([]);
  const [materialListData, setMaterialListData] = useState([]);
  const [itemizedData, setItemizedData] = useState([]);
  const [breakdownData, setBreakdownData] = useState([]);
  // Good/standard's detail lives in the three state vars above (populated
  // by applyPrices); Better/Best's detail is captured here so all
  // three tiers can be shown stacked at once instead of behind a switcher.
  const [crewBillDataBetter, setCrewBillDataBetter] = useState([]);
  const [crewBillDataBest, setCrewBillDataBest] = useState([]);
  const [materialListDataBetter, setMaterialListDataBetter] = useState([]);
  const [materialListDataBest, setMaterialListDataBest] = useState([]);
  const [breakdownDataBetter, setBreakdownDataBetter] = useState([]);
  const [breakdownDataBest, setBreakdownDataBest] = useState([]);
  const [profitMargin, setProfitMargin] = useState(0);
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [isSendToCustomerLoading, setIsSendToCustomerLoading] = useState(false);
  const [isLargerThan1200] = useMediaQuery('(min-width: 1200px)');
  const toast = useToast();
  const dispatch = useDispatch();
  const estimateStartPage = useSelector(
    state => state.calculator.estimateStartPage
  );
  const estimateSignaturePage = useSelector(
    state => state.calculator.estimateSignaturePage
  );
  const projectDescriptionLoaded = useSelector(
    state => state.calculator.estimateProjectDescription
  );
  const [projectDescription, setProjectDescription] = useState(
    projectDescriptionLoaded || ''
  );
  useEffect(() => {
    setProjectDescription(projectDescriptionLoaded || '');
  }, [projectDescriptionLoaded]);
  const hasPaymentOptionCash = useSelector(
    state => state.calculator.hasPaymentOptionCash
  );
  const hasPaymentOptionOne = useSelector(
    state => state.calculator.hasPaymentOptionOne
  );
  const hasPaymentOptionThree = useSelector(
    state => state.calculator.hasPaymentOptionThree
  );
  const hasPaymentOptionSeven = useSelector(
    state => state.calculator.hasPaymentOptionSeven
  );
  const oneYearDescription = useSelector(
    state => state.payment.options.oneYearDescription
  );
  const threeYearDescription = useSelector(
    state => state.payment.options.threeYearDescription
  );
  const sevenYearDescription = useSelector(
    state => state.payment.options.sevenYearDescription
  );
  const cashDescriptionLoaded = useSelector(
    state => state.payment.options.cashDescription
  );
  const standardEstimateDescriptionDefault = useSelector(
    state => state.calculator.standardEstimateDescriptionDefault
  );
  const betterEstimateDescriptionDefault = useSelector(
    state => state.calculator.betterEstimateDescriptionDefault
  );
  const bestEstimateDescriptionDefault = useSelector(
    state => state.calculator.bestEstimateDescriptionDefault
  );
  const standardEstimateTitleDefault = useSelector(
    state => state.calculator.standardEstimateTitleDefault
  );
  const betterEstimateTitleDefault = useSelector(
    state => state.calculator.betterEstimateTitleDefault
  );
  const bestEstimateTitleDefault = useSelector(
    state => state.calculator.bestEstimateTitleDefault
  );
  const estimateDescriptionLoaded = useSelector(
    state => state.calculator.estimateDescription
  );
  const [estimateDescription, setEstimateDescription] = useState(
    estimateDescriptionLoaded?.trim()
      ? estimateDescriptionLoaded
      : standardEstimateDescriptionDefault
  );
  const estimateTitleLoaded = useSelector(
    state => state.calculator.estimateTitle
  );
  const [estimateTitle, setEstimateTitle] = useState(
    estimateTitleLoaded?.trim()
      ? estimateTitleLoaded
      : standardEstimateTitleDefault
  );
  const estimatePriceLoaded = useSelector(
    state => state.calculator.estimateAmount
  );
  const [estimatePrice, setEstimatePrice] = useState(estimatePriceLoaded);
  const estimateDescriptionBetterLoaded = useSelector(
    state => state.calculator.estimateDescriptionBetter
  );
  const [estimateDescriptionBetter, setEstimateDescriptionBetter] = useState(
    estimateDescriptionBetterLoaded?.trim()
      ? estimateDescriptionBetterLoaded
      : betterEstimateDescriptionDefault
  );
  const estimateTitleBetterLoaded = useSelector(
    state => state.calculator.estimateTitleBetter
  );
  const [estimateTitleBetter, setEstimateTitleBetter] = useState(
    estimateTitleBetterLoaded?.trim()
      ? estimateTitleBetterLoaded
      : betterEstimateTitleDefault
  );
  const estimatePriceBetterLoaded = useSelector(
    state => state.calculator.estimateAmountBetter
  );
  const [estimatePriceBetter, setEstimatePriceBetter] = useState(
    estimatePriceBetterLoaded
  );

  const estimateDescriptionBestLoaded = useSelector(
    state => state.calculator.estimateDescriptionBest
  );
  const [estimateDescriptionBest, setEstimateDescriptionBest] = useState(
    estimateDescriptionBestLoaded?.trim()
      ? estimateDescriptionBestLoaded
      : bestEstimateDescriptionDefault
  );
  const estimateTitleBestLoaded = useSelector(
    state => state.calculator.estimateTitleBest
  );
  const [estimateTitleBest, setEstimateTitleBest] = useState(
    estimateTitleBestLoaded?.trim()
      ? estimateTitleBestLoaded
      : bestEstimateTitleDefault
  );
  const estimatePriceBestLoaded = useSelector(
    state => state.calculator.estimateAmountBest
  );
  const [estimatePriceBest, setEstimatePriceBest] = useState(
    estimatePriceBestLoaded
  );

  // Material defaults can load after this component mounts; fill any tier
  // that has no saved description yet so it isn't left blank.
  useEffect(() => {
    if (
      !estimateDescriptionLoaded?.trim() &&
      standardEstimateDescriptionDefault
    ) {
      setEstimateDescription(current =>
        current?.trim() ? current : standardEstimateDescriptionDefault
      );
    }
  }, [standardEstimateDescriptionDefault, estimateDescriptionLoaded]);
  useEffect(() => {
    if (
      !estimateDescriptionBetterLoaded?.trim() &&
      betterEstimateDescriptionDefault
    ) {
      setEstimateDescriptionBetter(current =>
        current?.trim() ? current : betterEstimateDescriptionDefault
      );
    }
  }, [betterEstimateDescriptionDefault, estimateDescriptionBetterLoaded]);
  useEffect(() => {
    if (
      !estimateDescriptionBestLoaded?.trim() &&
      bestEstimateDescriptionDefault
    ) {
      setEstimateDescriptionBest(current =>
        current?.trim() ? current : bestEstimateDescriptionDefault
      );
    }
  }, [bestEstimateDescriptionDefault, estimateDescriptionBestLoaded]);

  // The system-computed total per tier, kept separate from the editable
  // estimatePrice/Better/Best fields above so a manually-typed price never
  // gets silently overwritten by the next auto-sync -- this is just shown
  // alongside the input as a reference.
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [calculatedPriceBetter, setCalculatedPriceBetter] = useState(null);
  const [calculatedPriceBest, setCalculatedPriceBest] = useState(null);

  const upgradeOneDescription = useSelector(
    state => state.calculator.upgradeOneDescription
  );
  const upgradeOnePrice = useSelector(
    state => state.calculator.upgradeOnePrice
  );

  const upgradeTwoDescription = useSelector(
    state => state.calculator.upgradeTwoDescription
  );
  const upgradeTwoPrice = useSelector(
    state => state.calculator.upgradeTwoPrice
  );

  const upgradeThreeDescription = useSelector(
    state => state.calculator.upgradeThreeDescription
  );
  const upgradeThreePrice = useSelector(
    state => state.calculator.upgradeThreePrice
  );

  const estimateSendToEmailLoaded = useSelector(
    state => state.calculator.estimateSendToEmail
  );
  const [estimateSendToEmail, setEstimateSendToEmail] = useState(
    estimateSendToEmailLoaded
  );
  const estimateSendToNameLoaded = useSelector(
    state => state.calculator.estimateSendToName
  );
  const [showEstimatePdf, setShowEstimatePdf] = useState(false);
  const [estimateSendToName, setEstimateSendToName] = useState(
    estimateSendToNameLoaded
  );
  const [emailSubject, setEmailSubject] = useState('Roof Estimate');
  const [emailBody, setEmailBody] = useState(
    'Please review the agreement and provide your signature.'
  );
  const lastSentDateLoaded = useSelector(
    state => state.calculator.estimateLastSentDate
  );
  const [lastSentDate, setLastSentDate] = useState(lastSentDateLoaded);
  // When the link from the last send stops working. Cleared on a new send,
  // since that link is fresh.
  const linkExpiresLoaded = useSelector(
    state => state.calculator.estimateSigningTokenExpires
  );
  const [linkExpires, setLinkExpires] = useState(linkExpiresLoaded);
  useEffect(() => {
    setLinkExpires(linkExpiresLoaded);
  }, [linkExpiresLoaded]);
  const linkExpired =
    !!linkExpires && new Date(linkExpires).getTime() < Date.now();
  // What Mailgun reported for the last signing email ("" until it reports,
  // "delivered" or "failed"). Cleared on a new send.
  const emailStatusLoaded = useSelector(
    state => state.calculator.estimateEmailStatus
  );
  const emailFailReason = useSelector(
    state => state.calculator.estimateEmailFailReason
  );
  const [emailStatus, setEmailStatus] = useState(emailStatusLoaded);
  useEffect(() => {
    setEmailStatus(emailStatusLoaded);
  }, [emailStatusLoaded]);
  const estimateAccessedDateLoaded = useSelector(
    state => state.calculator.estimateAccessedDate
  );
  const [accessedDate, setAccessedDate] = useState(estimateAccessedDateLoaded);
  const estimateViewedDateLoaded = useSelector(
    state => state.calculator.estimateViewedDate
  );
  const [viewedDate, setViewedDate] = useState(estimateViewedDateLoaded);
  const estimateRejectedDateLoaded = useSelector(
    state => state.calculator.estimateRejectedDate
  );
  const [rejectedDate, setRejectedDate] = useState(estimateRejectedDateLoaded);
  const estimateRejectedReason = useSelector(
    state => state.calculator.estimateRejectedReason
  );
  const estimateSigned = useSelector(state => state.calculator.estimateSigned);
  const estimateSignedDate = useSelector(
    state => state.calculator.estimateSignedDate
  );
  const defaultProfitMargin = useSelector(
    state => state.payment.general.profitMargin
  );
  const modifiedProfitMargin = useSelector(
    state => state.calculator.profitMargin
  );
  const { user } = useUser();
  const {
    isOpen: isSendOpen,
    onOpen: onSendOpen,
    onClose: onSendClose,
  } = useDisclosure();
  const {
    isOpen: isSendToCustomerOpen,
    onOpen: onSendToCustomerOpen,
    onClose: onSendToCustomerClose,
  } = useDisclosure();
  const cancelSendRef = React.useRef();
  const cancelSendToCustomerRef = React.useRef();
  const [pdfBuffer, setPdfBuffer] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState('');
  const [estimatePdfBuffer, setEstimatePdfBuffer] = useState(null);
  const [estimatePdfBlobUrl, setEstimatePdfBlobUrl] = useState('');
  const [signedPdfBuffer, setSignedPdfBuffer] = useState(null);
  const [signedPdfBlobUrl, setSignedPdfBlobUrl] = useState('');
  // const {
  //   isOpen: isAddToCrewBillOpen,
  //   onOpen: onAddToCrewBillOpen,
  //   onClose: onAddToCrewBillClose,
  // } = useDisclosure();
  // const {
  //   isOpen: isAddToMaterialListOpen,
  //   onOpen: onAddToMaterialListOpen,
  //   onClose: onAddToMaterialListClose,
  // } = useDisclosure();
  const jobName = useSelector(state => state.calculator.jobName);
  const boldList = [
    'Price',
    'Price Per Square',
    'Commission After Tax',
    'Company Profit After Tax',
  ];

  const getCurrentDate = () => {
    const currentDate = new Date();
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const month = monthNames[currentDate.getMonth()];
    const day = currentDate.getDate();
    const year = currentDate.getFullYear();
    const formattedDate = `${month} ${day}, ${year}`;
    return formattedDate;
  };

  const convertUSDToNumber = input => {
    const beginning = input.slice(0, -3);
    const end = input.slice(-3);
    const filteredBeginning = beginning.replace(/\D/g, '');
    const returnNumber = parseFloat(filteredBeginning + end);
    return returnNumber;
  };

  const convertToUSD = input => {
    return input.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  function convertPercentToNumber(percentageString) {
    const number = parseFloat(percentageString.replace('%', ''));

    if (isNaN(number)) {
      return 0;
    }

    return number;
  }

  const onSendConfirm = async () => {
    setIsSendLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/email/send`,
        {
          username: user.email,
        },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      toast({
        title: 'Success',
        description: 'Estimate has been sent to your administrator.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (e) {
      console.log(e);
      toast({
        title: 'Error',
        description:
          'There was an error sending the estimate. Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    setIsSendLoading(false);
    onSendClose();
    onClose();
  };

  const onSendToCustomerConfirm = async () => {
    setIsSendToCustomerLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/pdf/send`,
        {
          username: user.email,
          sendToEmail: estimateSendToEmail,
          sendToName: estimateSendToName,
          emailSubject: emailSubject,
          emailBody: emailBody,
          estimateDescription: estimateDescription,
          estimatePrice: estimatePrice,
          estimateTitle: estimateTitle,
          estimateDescriptionBetter: estimateDescriptionBetter,
          estimatePriceBetter: estimatePriceBetter,
          estimateTitleBetter: estimateTitleBetter,
          estimateDescriptionBest: estimateDescriptionBest,
          estimatePriceBest: estimatePriceBest,
          estimateTitleBest: estimateTitleBest,
          upgradeOneDescription: upgradeOneDescription,
          upgradeOnePrice: upgradeOnePrice,
          upgradeTwoDescription: upgradeTwoDescription,
          upgradeTwoPrice: upgradeTwoPrice,
          upgradeThreeDescription: upgradeThreeDescription,
          upgradeThreePrice: upgradeThreePrice,
          projectDescription: projectDescription,
          customOptions: customOptions,
        },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      dispatch(updateEstimateProjectDescription(projectDescription));
      setLastSentDate(new Date());
      setLinkExpires('');
      setEmailStatus('');
      setAccessedDate('');
      setViewedDate('');
      setRejectedDate('');
      toast({
        title: 'Success',
        description: 'Estimate has been sent to the customer.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (e) {
      console.log(e);
      toast({
        title: 'Error',
        description:
          e.response?.data?.message ||
          'There was an error sending the estimate. Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    setIsSendToCustomerLoading(false);
    onSendToCustomerClose();
  };

  const downloadCsv = (data, suffix) => {
    const csvData = Papa.unparse(data);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    let fileName = jobName;
    if (fileName == '' || fileName == null) {
      fileName = 'Unnamed Job';
    }
    a.download = `${fileName} - ${suffix}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadCrewBill = tier => {
    setCrewBillLoading(true);
    try {
      const data = {
        standard: crewBillData,
        better: crewBillDataBetter,
        best: crewBillDataBest,
      }[tier];
      downloadCsv(data, `Crew Bill (${TIER_LABELS[tier]})`);
    } catch {
      setDownloadError(true);
      setCrewBillError(true);
    }
    setCrewBillLoading(false);
  };

  const downloadMaterialList = tier => {
    setMaterialListLoading(true);
    try {
      const data = {
        standard: materialListData,
        better: materialListDataBetter,
        best: materialListDataBest,
      }[tier];
      downloadCsv(data, `Material List (${TIER_LABELS[tier]})`);
    } catch {
      setDownloadError(true);
      setMaterialListError(true);
    }
    setMaterialListLoading(false);
  };

  const downloadItemizedList = async () => {
    setItemizedListLoading(true);
    try {
      downloadCsv(itemizedData, 'Itemized Estimate');
    } catch (error) {
      console.error(error);
      setDownloadError(true);
    }
    setItemizedListLoading(false);
  };

  const downloadBreakdown = tier => {
    setSummaryLoading(true);
    try {
      const data = {
        standard: breakdownData,
        better: breakdownDataBetter,
        best: breakdownDataBest,
      }[tier];
      downloadCsv(data, `Breakdown (${TIER_LABELS[tier]})`);
    } catch {
      setDownloadError(true);
      setBreakdownError(true);
    }
    setSummaryLoading(false);
  };

  const api = React.useMemo(() => (user ? estimateApi(user) : null), [user]);

  // Reload the estimate's photos, PDFs, custom options and the company
  // library each time the Finish screen opens (the estimate may have changed).
  useEffect(() => {
    if (!isOpen || !api) return;
    api
      .get('/api/pdf/estimate-content')
      .then(content => {
        setPhotoState({
          photos: content.photos,
          order: content.order,
          coverKey: content.coverKey,
        });
        setEstimateAttachments(content.attachments);
        if (content.photoPosition) setPhotoPosition(content.photoPosition);
        setCustomOptions(
          (content.customOptions || []).map(option => ({
            ...option,
            amount: option.amount ?? '',
          }))
        );
      })
      .catch(error => console.error('Could not load estimate content:', error));
    api
      .get('/api/pdf/library')
      .then(items => setLibraryItems(items || []))
      .catch(error =>
        console.error('Could not load attachment library:', error)
      );
  }, [isOpen, api]);

  const downloadAll = tier => {
    setAllLoading(true);
    downloadCrewBill(tier);
    downloadMaterialList(tier);
    downloadItemizedList();
    downloadBreakdown(tier);
    setAllLoading(false);
  };

  const handleSend = () => {
    // if (unsavedChanges) {
    //   handleUpdate();
    // }
    onSendOpen();
  };

  const handleSendToCustomer = () => {
    // if (unsavedChanges) {
    //   handleUpdate();
    // }
    onSendToCustomerOpen();
  };

  // Saves the cover description, pricing and upgrades. The store is updated
  // right away so it matches the fields; requests run one at a time so an
  // older one can't land after a newer one. Custom options save
  // themselves (CustomPricingOptions).
  const saveQueue = React.useRef(Promise.resolve());
  const saveEstimateText = (prices = {}) => {
    // `prices` can stand in for the tier prices (see handleViewPreview).
    const standardPrice = prices.standard ?? estimatePrice;
    const betterPrice = prices.better ?? estimatePriceBetter;
    const bestPrice = prices.best ?? estimatePriceBest;
    dispatch(updateEstimateProjectDescription(projectDescription));
    dispatch(updateEstimateDescription(estimateDescription));
    dispatch(updateEstimateAmount(standardPrice));
    dispatch(updateEstimateTitle(estimateTitle));
    dispatch(updateEstimateDescriptionBetter(estimateDescriptionBetter));
    dispatch(updateEstimateAmountBetter(betterPrice));
    dispatch(updateEstimateTitleBetter(estimateTitleBetter));
    dispatch(updateEstimateDescriptionBest(estimateDescriptionBest));
    dispatch(updateEstimateAmountBest(bestPrice));
    dispatch(updateEstimateTitleBest(estimateTitleBest));
    dispatch(updateUpgradeOneDescription(upgradeOneDescription));
    dispatch(updateUpgradeOnePrice(upgradeOnePrice));
    dispatch(updateUpgradeTwoDescription(upgradeTwoDescription));
    dispatch(updateUpgradeTwoPrice(upgradeTwoPrice));
    dispatch(updateUpgradeThreeDescription(upgradeThreeDescription));
    dispatch(updateUpgradeThreePrice(upgradeThreePrice));
    saveQueue.current = saveQueue.current
      .then(async () => {
        const token = await user.getIdToken();
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/roofs/update-estimate`,
          {
            username: user.email,
            sendToEmail: estimateSendToEmail,
            sendToName: estimateSendToName,
            estimateDescription: estimateDescription,
            estimateAmount: standardPrice,
            estimateTitle: estimateTitle,
            estimateDescriptionBetter: estimateDescriptionBetter,
            estimateAmountBetter: betterPrice,
            estimateTitleBetter: estimateTitleBetter,
            estimateDescriptionBest: estimateDescriptionBest,
            estimateAmountBest: bestPrice,
            estimateTitleBest: estimateTitleBest,
            upgradeOneDescription: upgradeOneDescription,
            upgradeOnePrice: upgradeOnePrice,
            upgradeTwoDescription: upgradeTwoDescription,
            upgradeTwoPrice: upgradeTwoPrice,
            upgradeThreeDescription: upgradeThreeDescription,
            upgradeThreePrice: upgradeThreePrice,
            projectDescription: projectDescription,
          },
          {
            headers: {
              authorization: `${token}`,
            },
          }
        );
      })
      .catch(() => {
        toast({
          title: 'Changes not saved',
          description: 'Check your connection and try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      });
    return saveQueue.current;
  };

  // Bumped when a field is left; the effect saves after the latest text
  // has rendered.
  const [saveRequest, setSaveRequest] = useState(0);
  const requestSave = () => setSaveRequest(count => count + 1);
  useEffect(() => {
    if (saveRequest && !estimateSigned) saveEstimateText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequest]);

  // Text edits can't change the calculated prices, so the preview only
  // rebuilds the PDF. A blank price still falls back to the calculated one.
  const handleViewPreview = async () => {
    try {
      const fill = (price, calculated) =>
        isPriceEmpty(price) && calculated !== null ? calculated : price;
      const prices = {
        standard: fill(estimatePrice, calculatedPrice),
        better: fill(estimatePriceBetter, calculatedPriceBetter),
        best: fill(estimatePriceBest, calculatedPriceBest),
      };
      setEstimatePrice(prices.standard);
      setEstimatePriceBetter(prices.better);
      setEstimatePriceBest(prices.best);
      await saveEstimateText(prices);
      setShowEstimatePdf(true);
      await getEstimatePdf();
      if (process.env.REACT_APP_ENVIRONMENT === 'prod') {
        mixpanel.track('User performed an action');
      }
    } catch {}
  };

  const handleUpdate = async () => {
    try {
      const token = await user.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/estimator/update/breakdown`,
        {
          username: user.email,
          appliedDiscount: appliedDiscount,
          profitMargin: modifiedProfitMargin,
          financeOption: financeOption,
        },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      await recalculatePrices();
      setUnsavedChanges(false);
      if (process.env.REACT_APP_ENVIRONMENT === 'prod') {
        mixpanel.track('User performed an action');
      }
    } catch {}
  };

  const handleUndo = async () => {
    try {
      const token = await user.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/estimator/undoupdate/breakdown`,
        {
          username: user.email,
        },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      dispatch(updateProfitMargin(0));
      dispatch(updateFinanceOption('cash'));
      await recalculatePrices();
      setUnsavedChanges(false);
    } catch {}
  };

  const getItemizedPdf = async inputData => {
    try {
      setGetItemizedPdfLoading(true);
      const token = await user.getIdToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/pdf/generate`,
        {
          headers: {
            authorization: `${token}`,
          },
          params: {
            username: user.email,
            csv: inputData.csv,
            stringCsv: JSON.stringify(inputData.csv),
          },
        }
      );
      setPdfBuffer(response.data.data);
      setGetItemizedPdfLoading(false);
      // downloadPdf(response.data.data);
    } catch (error) {
      setGetItemizedPdfLoading(false);
      console.error(error);
    }
  };

  const getEstimatePdf = async inputData => {
    try {
      setGetEstimatePdfLoading(true);
      const token = await user.getIdToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/pdf/generate-estimate`,
        {
          headers: {
            authorization: `${token}`,
          },
          params: {
            username: user.email,
          },
        }
      );
      setEstimatePdfBuffer(response.data.data);
      setGetEstimatePdfLoading(false);
    } catch (error) {
      setGetEstimatePdfLoading(false);
      console.error(error);
    }
  };

  const getSignedPdf = async inputData => {
    try {
      setGetSignedPdfLoading(true);
      const token = await user.getIdToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/pdf/signed`,
        {
          headers: {
            authorization: `${token}`,
          },
          params: {
            username: user.email,
          },
        }
      );
      setSignedPdfBuffer(response.data.data);
      setGetSignedPdfLoading(false);
    } catch (error) {
      setGetSignedPdfLoading(false);
      console.error(error);
    }
  };

  const downloadPdf = () => {
    setPdfLoading(true);
    // Create a Blob from the PDF buffer
    const blob = new Blob([new Uint8Array(pdfBuffer)], {
      type: 'application/pdf',
    });

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement('a');
    link.href = url;
    let fileName = jobName;
    if (fileName == '' || fileName == null) {
      fileName = 'Unnamed Job';
    }
    link.download = `${fileName} - Itemized Estimate`;

    // Append the link to the document body
    document.body.appendChild(link);

    // Trigger the download by programmatically clicking the link
    link.click();

    // Clean up and remove the link
    document.body.removeChild(link);

    // Revoke the URL after the download
    URL.revokeObjectURL(url);
    setPdfLoading(false);
  };

  const downloadEstimatePdf = () => {
    setEstimatePdfLoading(true);
    // Create a Blob from the PDF buffer
    const blob = new Blob([new Uint8Array(estimatePdfBuffer)], {
      type: 'application/pdf',
    });

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement('a');
    link.href = url;
    let fileName = jobName;
    if (fileName == '' || fileName == null) {
      fileName = 'Unnamed Job';
    }
    link.download = `${fileName} - Estimate`;

    // Append the link to the document body
    document.body.appendChild(link);

    // Trigger the download by programmatically clicking the link
    link.click();

    // Clean up and remove the link
    document.body.removeChild(link);

    // Revoke the URL after the download
    URL.revokeObjectURL(url);
    setEstimatePdfLoading(false);
  };

  const downloadSignedPdf = () => {
    setSignedPdfLoading(true);
    // Create a Blob from the PDF buffer
    const blob = new Blob([new Uint8Array(signedPdfBuffer)], {
      type: 'application/pdf',
    });

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement('a');
    link.href = url;
    let fileName = jobName;
    if (fileName == '' || fileName == null) {
      fileName = 'Unnamed Job';
    }
    link.download = `${fileName} - Signed`;

    // Append the link to the document body
    document.body.appendChild(link);

    // Trigger the download by programmatically clicking the link
    link.click();

    // Clean up and remove the link
    document.body.removeChild(link);

    // Revoke the URL after the download
    URL.revokeObjectURL(url);
    setSignedPdfLoading(false);
  };

  useEffect(() => {
    if (pdfBuffer) {
      // const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
      const blob = new Blob([new Uint8Array(pdfBuffer)], {
        type: 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);

      // Cleanup function to revoke the object URL
      return () => URL.revokeObjectURL(url);
    }
  }, [pdfBuffer]);

  useEffect(() => {
    if (estimatePdfBuffer) {
      // const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
      const blob = new Blob([new Uint8Array(estimatePdfBuffer)], {
        type: 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      setEstimatePdfBlobUrl(url);

      // Cleanup function to revoke the object URL
      return () => URL.revokeObjectURL(url);
    }
  }, [estimatePdfBuffer]);

  useEffect(() => {
    if (signedPdfBuffer) {
      // const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
      const blob = new Blob([new Uint8Array(signedPdfBuffer)], {
        type: 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      setSignedPdfBlobUrl(url);

      // Cleanup function to revoke the object URL
      return () => URL.revokeObjectURL(url);
    }
  }, [signedPdfBuffer]);

  const handleAddCustomItem = async () => {
    try {
      const token = await user.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/roofs/add_itemized_custom_item`,
        {
          username: user.email,
          description: customItemDescription,
          quantity: customItemQuantity,
          rate: customItemRate,
        },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      setCustomItemDescription('');
      setCustomItemQuantity(0);
      setCustomItemRate(0);
      await recalculatePrices();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteCustomItems = async () => {
    try {
      const token = await user.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/roofs/delete_itemized_custom_items`,
        { username: user.email },
        {
          headers: {
            authorization: `${token}`,
          },
        }
      );
      await recalculatePrices();
    } catch (e) {
      console.error(e);
    }
  };

  // Recalculates Standard, Better and Best in one request (the server prices
  // all three without switching the roof's saved tier). Calculations can
  // overlap, e.g. a save while one is running; only the newest one's results
  // are used, so an older result never overwrites a newer one.
  const latestCalculation = React.useRef(0);
  const calculating = React.useRef(false);
  // A save is waiting on the debounce to recalculate.
  const changePending = React.useRef(false);
  // When the last calculation finished without errors (0 = never).
  const lastCalculatedAt = React.useRef(0);
  // The debounced call below is created once, so it goes through this ref to
  // use the current state (e.g. which prices are blank).
  const recalculateRef = React.useRef(null);

  const recalculatePrices = async () => {
    if (!user) return;
    const run = ++latestCalculation.current;
    calculating.current = true;
    changePending.current = false;
    setDownloadError(false);
    setCalculateLoading(true);
    setIsCalculateLoading(true);
    try {
      const token = await user.getIdToken();
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/estimator/prices`,
        {
          headers: { authorization: `${token}` },
          params: { username: user.email },
        }
      );
      if (run !== latestCalculation.current) return;
      applyPrices(data);
      lastCalculatedAt.current = Date.now();
    } catch (e) {
      if (run !== latestCalculation.current) return;
      console.error(e);
      setDownloadError(true);
    }
    calculating.current = false;
    setIsCalculateLoading(false);
    setCalculateLoading(false);
  };
  recalculateRef.current = recalculatePrices;

  // A tier's price box is filled with its calculated price only when blank;
  // a price the user typed is kept.
  const fillBlankPrice = (current, total, setPrice, updateAmount) => {
    if (isPriceEmpty(current)) {
      setPrice(total);
      dispatch(updateAmount(total));
    }
  };

  const applyPrices = ({ standard, better, best, redeckTotal }) => {
    setCrewBillDataBetter(better.crew.csv);
    setMaterialListDataBetter(better.material.csv);
    setBreakdownDataBetter(better.breakdown);
    const betterTotal = convertUSDToNumber(better.total);
    setCalculatedPriceBetter(betterTotal);
    fillBlankPrice(
      estimatePriceBetter,
      betterTotal,
      setEstimatePriceBetter,
      updateEstimateAmountBetter
    );

    setCrewBillDataBest(best.crew.csv);
    setMaterialListDataBest(best.material.csv);
    setBreakdownDataBest(best.breakdown);
    const bestTotal = convertUSDToNumber(best.total);
    setCalculatedPriceBest(bestTotal);
    fillBlankPrice(
      estimatePriceBest,
      bestTotal,
      setEstimatePriceBest,
      updateEstimateAmountBest
    );

    dispatch(updateShingleOption('standard'));
    setCrewBillData(standard.crew.csv);
    setMaterialListData(standard.material.csv);
    setItemizedData(standard.itemized.csv);
    setBreakdownData(standard.breakdown);

    setPricePerMonth('$00.00');
    standard.breakdown.forEach(item => {
      if (item[0] === 'Margin') {
        setMargin(item[1]);
        setMarginPercent(item[2]);
        setProfitMargin(convertPercentToNumber(item[2]));
      } else if (item[0] === 'Applied Discount') {
        setAppliedDiscount(convertPercentToNumber(item[2]));
      } else if (item[0] === 'Available Discount') {
        setAvailableDiscount(convertPercentToNumber(item[2]));
        setAvailableDollarDiscount(item[1]);
      } else if (item[0] === 'Price') {
        setTotalPrice(item[1]);
      } else if (item[0] === 'Profit') {
        setGrossProfit(item[1]);
        setGrossProfitPercent(item[2]);
      } else if (item[0] === 'Overhead') {
        setOverhead(item[1]);
        setOverheadPercent(item[2]);
      } else if (item[0] === 'Commission') {
        setSalesCommission(item[1]);
        setSalesCommissionPercent(item[2]);
      } else if (item[0] === 'Commission After Tax') {
        setAfterTaxSalesCommission(item[1]);
        setAfterTaxSalesCommissionPercent(item[2]);
      } else if (item[0] === 'Company Profit') {
        setCompanyNet(item[1]);
        setCompanyNetPercent(item[2]);
      } else if (item[0] === 'Company Profit After Tax') {
        setAfterTaxCompanyNet(item[1]);
        setAfterTaxCompanyNetPercent(item[2]);
      } else if (item[0].includes('Price per month')) {
        setPricePerMonth(item[1]);
      } else if (item[0] === 'Price Per Square') {
        setPricePerSq(item[1]);
      }
    });

    // standard.total is "$00.00" when the breakdown has no Price row (an
    // incomplete estimate), which still needs to reach the UI.
    const standardTotal = convertUSDToNumber(standard.total);
    setCalculatedPrice(standardTotal);
    fillBlankPrice(
      estimatePrice,
      standardTotal,
      setEstimatePrice,
      updateEstimateAmount
    );

    setIfRedeckRequired(
      convertToUSD(convertUSDToNumber(redeckTotal) - standardTotal)
    );
    getItemizedPdf(standard.itemized);
    getEstimatePdf();
    if (estimateSigned) {
      getSignedPdf();
    }
  };

  const debouncedRecalculate = useCallback(
    debounce(() => {
      if (user) {
        recalculateRef.current();
      }
    }, 2000),
    [user]
  );

  const calculatedOnLoad = React.useRef(false);

  const handleTabChange = index => {
    setSelectedTab(index);
  };

  useEffect(() => {
    if (!calculateButtonClicked || !user) return;
    // The first request is the page load: nothing to batch, so start now.
    // Later ones follow saves and wait in case more saves come.
    if (calculatedOnLoad.current) {
      changePending.current = true;
      debouncedRecalculate();
    } else {
      calculatedOnLoad.current = true;
      recalculatePrices();
    }
    setCalculateButtonClicked(false);
  }, [calculateButtonClicked, user]);

  useEffect(() => {
    return () => {
      debouncedRecalculate.cancel();
    };
  }, [debouncedRecalculate]);

  useEffect(() => {
    // Opening reuses the last results unless a save is waiting to be
    // calculated or the results are old enough that settings may have
    // changed elsewhere (e.g. another admin). A calculation already running
    // just finishes.
    if (!isOpen) return;
    refreshEstimateStatus();
    const stale =
      Date.now() - lastCalculatedAt.current > RECALCULATE_ON_OPEN_AFTER_MS;
    if (changePending.current) {
      debouncedRecalculate.cancel();
      recalculatePrices();
    } else if (stale && !calculating.current) {
      recalculatePrices();
    }
  }, [isOpen]);

  // Reloads only where the estimate stands with the customer (delivered,
  // opened, viewed, declined, signed), not the whole estimate.
  const [statusRefreshing, setStatusRefreshing] = useState(false);
  const refreshEstimateStatus = async () => {
    if (!api || statusRefreshing) return;
    setStatusRefreshing(true);
    try {
      const status = await api.get('/api/roofs/estimate-status');
      // The local copies are set too: they don't follow the store after a send.
      setLastSentDate(status.estimateLastSentDate || '');
      setLinkExpires(status.estimateSigningTokenExpires || '');
      setEmailStatus(status.estimateEmailStatus);
      setAccessedDate(status.estimateAccessedDate || '');
      setViewedDate(status.estimateViewedDate || '');
      setRejectedDate(status.estimateRejectedDate || '');
      dispatch(updateEstimateLastSentDate(status.estimateLastSentDate || ''));
      dispatch(
        updateEstimateSigningTokenExpires(
          status.estimateSigningTokenExpires || ''
        )
      );
      dispatch(
        updateEstimateEmailStatus({
          status: status.estimateEmailStatus,
          reason: status.estimateEmailFailReason,
        })
      );
      dispatch(updateEstimateAccessedDate(status.estimateAccessedDate || ''));
      dispatch(updateEstimateViewedDate(status.estimateViewedDate || ''));
      dispatch(updateEstimateRejectedDate(status.estimateRejectedDate || ''));
      dispatch(updateEstimateRejectedReason(status.estimateRejectedReason));
      if (status.estimateSigned && !estimateSigned) {
        dispatch(updateEstimateSignedDate(status.estimateSignedDate || ''));
        dispatch(updateEstimateSigned(true));
      }
    } catch (e) {
      console.error(e);
    }
    setStatusRefreshing(false);
  };

  // The customer signed since the estimate was loaded (found by the refresh
  // above): recalculate everything so the signed PDF loads with it.
  const wasSigned = React.useRef(estimateSigned);
  useEffect(() => {
    if (estimateSigned && !wasSigned.current) recalculatePrices();
    wasSigned.current = estimateSigned;
  }, [estimateSigned]);

  const isPriceEmpty = price =>
    price === '' || price === null || price === undefined;

  const isStandardFilled =
    !!estimateTitle?.trim() || !!estimateDescription?.trim();
  const isBetterFilled =
    !!estimateTitleBetter?.trim() || !!estimateDescriptionBetter?.trim();
  const isBestFilled =
    !!estimateTitleBest?.trim() || !!estimateDescriptionBest?.trim();

  const filledCustomOptions = customOptions.filter(option =>
    option.title?.trim()
  );
  const pricingOptionCount =
    [isStandardFilled, isBetterFilled, isBestFilled].filter(Boolean).length +
    filledCustomOptions.length;
  const hasAnyOptionFilled = pricingOptionCount > 0;
  const hasOptionMissingPrice =
    (isStandardFilled && isPriceEmpty(estimatePrice)) ||
    (isBetterFilled && isPriceEmpty(estimatePriceBetter)) ||
    (isBestFilled && isPriceEmpty(estimatePriceBest)) ||
    filledCustomOptions.some(option => isPriceEmpty(option.amount));

  // Options that would go out priced at $0 (filled in, price entered as 0).
  const isZeroPrice = price => !isPriceEmpty(price) && Number(price) === 0;
  const zeroPricedOptions = [
    isStandardFilled &&
      isZeroPrice(estimatePrice) &&
      (estimateTitle?.trim() || 'Standard'),
    isBetterFilled &&
      isZeroPrice(estimatePriceBetter) &&
      (estimateTitleBetter?.trim() || 'Better'),
    isBestFilled &&
      isZeroPrice(estimatePriceBest) &&
      (estimateTitleBest?.trim() || 'Best'),
    ...filledCustomOptions
      .filter(option => isZeroPrice(option.amount))
      .map(option => option.title.trim()),
  ].filter(Boolean);

  const hasUpgradeMissingPrice =
    (!!upgradeOneDescription?.trim() && isPriceEmpty(upgradeOnePrice)) ||
    (!!upgradeTwoDescription?.trim() && isPriceEmpty(upgradeTwoPrice)) ||
    (!!upgradeThreeDescription?.trim() && isPriceEmpty(upgradeThreePrice));

  const canSendEstimate =
    hasAnyOptionFilled && !hasOptionMissingPrice && !hasUpgradeMissingPrice;

  const isCustomerEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    estimateSendToEmail?.trim() || ''
  );
  const isCustomerNameFilled = !!estimateSendToName?.trim();

  return (
    <>
      <Modal isOpen={isOpen} size="custom" onClose={onClose}>
        <ModalOverlay>
          <ModalContent
            maxW={{ base: '100vw', md: '80vw' }}
            maxH={{ base: '100dvh', md: '85vh' }}
            minH={{ base: '100dvh', md: 'auto' }}
            mx={{ base: 0, md: 'auto' }}
            my={{ base: 0, md: '3.75rem' }}
            borderRadius={{ base: 0, md: 'md' }}
            overflowY="auto"
          >
            <ModalHeader fontSize="lg" fontWeight="bold"></ModalHeader>
            <ModalCloseButton />
            <ModalBody marginBottom="50px" px={{ base: 3, md: 6 }}>
              {calculateLoading ? (
                <Center>
                  <VStack>
                    <Spinner
                      thickness="6px"
                      speed="1.25s"
                      size="lg"
                      emptyColor="gray.200"
                      color="black.500"
                    />
                    <Text>Calculating...</Text>
                  </VStack>
                </Center>
              ) : (
                <Center w="100%">
                  <VStack spacing="20px" w="100%">
                    <Tabs
                      variant="soft-rounded"
                      colorScheme="blue"
                      align="center"
                      size={{ base: 'sm', md: 'md' }}
                      index={selectedTab}
                      onChange={handleTabChange}
                      w="100%"
                    >
                      <TabList
                        overflowX="auto"
                        overflowY="hidden"
                        flexWrap="nowrap"
                        gap={2}
                        pb={2}
                        sx={{
                          '::-webkit-scrollbar': { height: '4px' },
                          scrollbarWidth: 'thin',
                        }}
                      >
                        <Tab flexShrink={0} fontWeight="semibold">
                          All
                        </Tab>
                        <Tab flexShrink={0} fontWeight="semibold">
                          Estimate
                        </Tab>
                        {estimateSigned && (
                          <Tab flexShrink={0} fontWeight="semibold">
                            Signed
                          </Tab>
                        )}
                        <Tab flexShrink={0} fontWeight="semibold">
                          Breakdown
                        </Tab>
                        <Tab flexShrink={0} fontWeight="semibold">
                          Crew Bill
                        </Tab>
                        <Tab flexShrink={0} fontWeight="semibold">
                          Material List
                        </Tab>
                        <Tab flexShrink={0} fontWeight="semibold">
                          Itemized
                        </Tab>
                        <Tab flexShrink={0} fontWeight="semibold">
                          Itemized PDF
                        </Tab>
                      </TabList>
                      <TabPanels>
                        <TabPanel>
                          <VStack spacing={3}>
                            {['standard', 'better', 'best'].map(tier => (
                              <Button
                                key={tier}
                                marginY="4px"
                                leftIcon={<FaDownload />}
                                colorScheme="green"
                                size="sm"
                                onClick={() => downloadAll(tier)}
                                isLoading={allLoading}
                              >
                                <Text fontSize="xs">
                                  Download All ({TIER_LABELS[tier]})
                                </Text>
                              </Button>
                            ))}
                          </VStack>
                        </TabPanel>
                        <TabPanel>
                          <VStack
                            spacing={5}
                            align="stretch"
                            maxW="900px"
                            mx="auto"
                            py={2}
                          >
                            <SectionCard title="Cover">
                              <Stack
                                direction={{ base: 'column', md: 'row' }}
                                align={{ base: 'stretch', md: 'flex-start' }}
                                spacing={{ base: 2, md: 4 }}
                              >
                                <Text
                                  fontSize="xs"
                                  fontWeight="bold"
                                  color="gray.600"
                                  w={{ md: '100px' }}
                                  flexShrink={0}
                                  pt={{ md: 2 }}
                                >
                                  DESCRIPTION
                                </Text>
                                <Textarea
                                  onBlur={requestSave}
                                  value={projectDescription}
                                  onChange={e =>
                                    setProjectDescription(e.target.value)
                                  }
                                  placeholder="A short summary of the project, e.g. full replacement of the storm-damaged roof on the house and garage."
                                  rows={3}
                                  maxLength={600}
                                  isDisabled={estimateSigned}
                                />
                              </Stack>

                              <Stack
                                direction={{ base: 'column', md: 'row' }}
                                align={{ base: 'stretch', md: 'center' }}
                                spacing={{ base: 2, md: 4 }}
                              >
                                <Text
                                  fontSize="xs"
                                  fontWeight="bold"
                                  color="gray.600"
                                  w={{ md: '100px' }}
                                  flexShrink={0}
                                >
                                  PHOTO
                                </Text>
                                {api && (
                                  <EstimateCoverPhoto
                                    api={api}
                                    state={photoState}
                                    onChange={setPhotoState}
                                    isDisabled={estimateSigned}
                                    toast={toast}
                                  />
                                )}
                              </Stack>
                            </SectionCard>

                            <SectionCard title="Photos">
                              {api && (
                                <EstimatePhotos
                                  api={api}
                                  state={photoState}
                                  onChange={setPhotoState}
                                  isDisabled={estimateSigned}
                                  toast={toast}
                                />
                              )}
                            </SectionCard>

                            <SectionCard title="Attachments">
                              {api && (
                                <EstimateAttachments
                                  api={api}
                                  attachments={estimateAttachments}
                                  onChange={setEstimateAttachments}
                                  photoPosition={photoPosition}
                                  onPhotoPositionChange={setPhotoPosition}
                                  libraryItems={libraryItems}
                                  photoPageCount={Math.ceil(
                                    photoState.order.length / PHOTOS_PER_PAGE
                                  )}
                                  pricingOptionCount={pricingOptionCount}
                                  isDisabled={estimateSigned}
                                  toast={toast}
                                />
                              )}
                            </SectionCard>

                            <SectionCard title="Pricing">
                              <Box>
                                <HStack align="flex-start" spacing={4} w="100%">
                                  <VStack flex={1} align="stretch">
                                    <Text
                                      fontSize="xs"
                                      fontWeight="bold"
                                      color="gray.600"
                                      textAlign="left"
                                    >
                                      STANDARD
                                    </Text>
                                    <Input
                                      onBlur={requestSave}
                                      isReadOnly={estimateSigned}
                                      value={estimateTitle}
                                      onChange={e =>
                                        setEstimateTitle(e.target.value)
                                      }
                                      placeholder="Enter an option..."
                                    />

                                    <NumberInput
                                      isReadOnly={estimateSigned}
                                      value={estimatePrice}
                                      width="100%"
                                      onChange={(_, valueAsNumber) =>
                                        setEstimatePrice(
                                          Number.isNaN(valueAsNumber)
                                            ? ''
                                            : valueAsNumber
                                        )
                                      }
                                      min={0}
                                      precision={2}
                                    >
                                      <NumberInputField
                                        placeholder="0.00"
                                        onBlur={requestSave}
                                      />
                                    </NumberInput>
                                    {calculatedPrice !== null && (
                                      <Text fontSize="xs" color="gray.500">
                                        Calculated:{' '}
                                        {convertToUSD(calculatedPrice)}
                                      </Text>
                                    )}
                                  </VStack>
                                  <Box flex={2}>
                                    <Textarea
                                      onBlur={requestSave}
                                      isReadOnly={estimateSigned}
                                      value={estimateDescription}
                                      onChange={e =>
                                        setEstimateDescription(e.target.value)
                                      }
                                      placeholder="Enter a description..."
                                      rows={6}
                                    />
                                    <MaterialDescriptionHint
                                      value={estimateDescription}
                                      materialValue={
                                        standardEstimateDescriptionDefault
                                      }
                                      onUse={() => {
                                        setEstimateDescription(
                                          standardEstimateDescriptionDefault
                                        );
                                        requestSave();
                                      }}
                                      isDisabled={estimateSigned}
                                    />
                                  </Box>
                                </HStack>
                              </Box>

                              <Divider />

                              <Box>
                                <HStack align="flex-start" spacing={4} w="100%">
                                  <VStack flex={1} align="stretch">
                                    <Text
                                      fontSize="xs"
                                      fontWeight="bold"
                                      color="gray.600"
                                      textAlign="left"
                                    >
                                      BETTER
                                    </Text>
                                    <Input
                                      onBlur={requestSave}
                                      isReadOnly={estimateSigned}
                                      value={estimateTitleBetter}
                                      onChange={e =>
                                        setEstimateTitleBetter(e.target.value)
                                      }
                                      placeholder="Enter an option..."
                                    />

                                    <NumberInput
                                      isReadOnly={estimateSigned}
                                      value={estimatePriceBetter}
                                      width="100%"
                                      onChange={(_, valueAsNumber) =>
                                        setEstimatePriceBetter(
                                          Number.isNaN(valueAsNumber)
                                            ? ''
                                            : valueAsNumber
                                        )
                                      }
                                      min={0}
                                      precision={2}
                                    >
                                      <NumberInputField
                                        placeholder="0.00"
                                        onBlur={requestSave}
                                      />
                                    </NumberInput>
                                    {calculatedPriceBetter !== null && (
                                      <Text fontSize="xs" color="gray.500">
                                        Calculated:{' '}
                                        {convertToUSD(calculatedPriceBetter)}
                                      </Text>
                                    )}
                                  </VStack>
                                  <Box flex={2}>
                                    <Textarea
                                      onBlur={requestSave}
                                      isReadOnly={estimateSigned}
                                      value={estimateDescriptionBetter}
                                      onChange={e =>
                                        setEstimateDescriptionBetter(
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter a description..."
                                      rows={6}
                                    />
                                    <MaterialDescriptionHint
                                      value={estimateDescriptionBetter}
                                      materialValue={
                                        betterEstimateDescriptionDefault
                                      }
                                      onUse={() => {
                                        setEstimateDescriptionBetter(
                                          betterEstimateDescriptionDefault
                                        );
                                        requestSave();
                                      }}
                                      isDisabled={estimateSigned}
                                    />
                                  </Box>
                                </HStack>
                              </Box>

                              <Divider />

                              <Box>
                                <HStack align="flex-start" spacing={4} w="100%">
                                  <VStack flex={1} align="stretch">
                                    <Text
                                      fontSize="xs"
                                      fontWeight="bold"
                                      color="gray.600"
                                      textAlign="left"
                                    >
                                      BEST
                                    </Text>
                                    <Input
                                      onBlur={requestSave}
                                      isReadOnly={estimateSigned}
                                      value={estimateTitleBest}
                                      onChange={e =>
                                        setEstimateTitleBest(e.target.value)
                                      }
                                      placeholder="Enter an option..."
                                    />

                                    <NumberInput
                                      isReadOnly={estimateSigned}
                                      value={estimatePriceBest}
                                      width="100%"
                                      onChange={(_, valueAsNumber) =>
                                        setEstimatePriceBest(
                                          Number.isNaN(valueAsNumber)
                                            ? ''
                                            : valueAsNumber
                                        )
                                      }
                                      min={0}
                                      precision={2}
                                    >
                                      <NumberInputField
                                        placeholder="0.00"
                                        onBlur={requestSave}
                                      />
                                    </NumberInput>
                                    {calculatedPriceBest !== null && (
                                      <Text fontSize="xs" color="gray.500">
                                        Calculated:{' '}
                                        {convertToUSD(calculatedPriceBest)}
                                      </Text>
                                    )}
                                  </VStack>
                                  <Box flex={2}>
                                    <Textarea
                                      onBlur={requestSave}
                                      isReadOnly={estimateSigned}
                                      value={estimateDescriptionBest}
                                      onChange={e =>
                                        setEstimateDescriptionBest(
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter a description..."
                                      rows={6}
                                    />
                                    <MaterialDescriptionHint
                                      value={estimateDescriptionBest}
                                      materialValue={
                                        bestEstimateDescriptionDefault
                                      }
                                      onUse={() => {
                                        setEstimateDescriptionBest(
                                          bestEstimateDescriptionDefault
                                        );
                                        requestSave();
                                      }}
                                      isDisabled={estimateSigned}
                                    />
                                  </Box>
                                </HStack>
                              </Box>

                              <Divider />

                              {api && (
                                <CustomPricingOptions
                                  api={api}
                                  options={customOptions}
                                  onChange={setCustomOptions}
                                  isDisabled={estimateSigned}
                                  toast={toast}
                                />
                              )}

                              <Box>
                                <Text
                                  fontSize="xs"
                                  fontWeight="bold"
                                  color="gray.600"
                                  mb={2}
                                >
                                  UPGRADES (OPTIONAL)
                                </Text>
                                <VStack spacing={3} align="stretch">
                                  <HStack
                                    align="flex-start"
                                    spacing={4}
                                    w="100%"
                                  >
                                    <Input
                                      onBlur={requestSave}
                                      isReadOnly={estimateSigned}
                                      flex={2}
                                      value={upgradeOneDescription}
                                      onChange={e =>
                                        dispatch(
                                          updateUpgradeOneDescription(
                                            e.target.value
                                          )
                                        )
                                      }
                                      placeholder="Enter upgrade..."
                                    />
                                    <NumberInput
                                      isReadOnly={estimateSigned}
                                      flex={1}
                                      value={upgradeOnePrice}
                                      width="100%"
                                      onChange={(_, valueAsNumber) =>
                                        dispatch(
                                          updateUpgradeOnePrice(
                                            Number.isNaN(valueAsNumber)
                                              ? ''
                                              : valueAsNumber
                                          )
                                        )
                                      }
                                      min={0}
                                      precision={2}
                                    >
                                      <NumberInputField
                                        placeholder="0.00"
                                        onBlur={requestSave}
                                      />
                                    </NumberInput>
                                  </HStack>

                                  <HStack
                                    align="flex-start"
                                    spacing={4}
                                    w="100%"
                                  >
                                    <Input
                                      onBlur={requestSave}
                                      isReadOnly={estimateSigned}
                                      flex={2}
                                      value={upgradeTwoDescription}
                                      onChange={e =>
                                        dispatch(
                                          updateUpgradeTwoDescription(
                                            e.target.value
                                          )
                                        )
                                      }
                                      placeholder="Enter upgrade..."
                                    />
                                    <NumberInput
                                      isReadOnly={estimateSigned}
                                      flex={1}
                                      value={upgradeTwoPrice}
                                      width="100%"
                                      onChange={(_, valueAsNumber) =>
                                        dispatch(
                                          updateUpgradeTwoPrice(
                                            Number.isNaN(valueAsNumber)
                                              ? ''
                                              : valueAsNumber
                                          )
                                        )
                                      }
                                      min={0}
                                      precision={2}
                                    >
                                      <NumberInputField
                                        placeholder="0.00"
                                        onBlur={requestSave}
                                      />
                                    </NumberInput>
                                  </HStack>

                                  <HStack
                                    align="flex-start"
                                    spacing={4}
                                    w="100%"
                                  >
                                    <Input
                                      onBlur={requestSave}
                                      isReadOnly={estimateSigned}
                                      flex={2}
                                      value={upgradeThreeDescription}
                                      onChange={e =>
                                        dispatch(
                                          updateUpgradeThreeDescription(
                                            e.target.value
                                          )
                                        )
                                      }
                                      placeholder="Enter upgrade..."
                                    />
                                    <NumberInput
                                      isReadOnly={estimateSigned}
                                      flex={1}
                                      value={upgradeThreePrice}
                                      width="100%"
                                      onChange={(_, valueAsNumber) =>
                                        dispatch(
                                          updateUpgradeThreePrice(
                                            Number.isNaN(valueAsNumber)
                                              ? ''
                                              : valueAsNumber
                                          )
                                        )
                                      }
                                      min={0}
                                      precision={2}
                                    >
                                      <NumberInputField
                                        placeholder="0.00"
                                        onBlur={requestSave}
                                      />
                                    </NumberInput>
                                  </HStack>
                                </VStack>
                              </Box>

                              <HStack justify="center" spacing={3}>
                                <Button
                                  colorScheme="blue"
                                  size="sm"
                                  onClick={handleViewPreview}
                                  isLoading={estimatePdfLoading}
                                  isDisabled={estimateSigned}
                                >
                                  <Text fontSize="xs">View Preview</Text>
                                </Button>
                                {estimateSigned && (
                                  <SignedBadge dateValue={estimateSignedDate} />
                                )}
                              </HStack>
                            </SectionCard>

                            {estimatePdfBlobUrl && showEstimatePdf && (
                              <SectionCard title="Preview">
                                <Center>
                                  <Box
                                    border="2px solid black"
                                    display="inline-block"
                                  >
                                    {getEstimatePdfLoading ? (
                                      <Center>
                                        <VStack>
                                          <Spinner
                                            thickness="6px"
                                            speed="1.25s"
                                            size="lg"
                                            emptyColor="gray.200"
                                            color="black.500"
                                          />
                                          <Text>Calculating...</Text>
                                        </VStack>
                                      </Center>
                                    ) : (
                                      <Document
                                        file={estimatePdfBlobUrl}
                                        onLoadSuccess={({ numPages }) =>
                                          setEstimatePdfPageCount(numPages)
                                        }
                                      >
                                        <SimpleGrid
                                          columns={{ base: 2, md: 4 }}
                                          spacing={4}
                                          p={2}
                                        >
                                          {Array.from(
                                            { length: estimatePdfPageCount },
                                            (_, i) => (
                                              <Page
                                                key={i}
                                                pageNumber={i + 1}
                                                width={180}
                                              />
                                            )
                                          )}
                                        </SimpleGrid>
                                      </Document>
                                    )}
                                    <Button
                                      marginY="10px"
                                      leftIcon={<FaDownload />}
                                      colorScheme="blue"
                                      size="sm"
                                      onClick={downloadEstimatePdf}
                                      isLoading={estimatePdfLoading}
                                    >
                                      <Text fontSize="xs">Download</Text>
                                    </Button>
                                  </Box>
                                </Center>
                              </SectionCard>
                            )}

                            <SectionCard title="Send to Customer">
                              <HStack spacing={3}>
                                <Input
                                  value={estimateSendToName}
                                  isReadOnly={estimateSigned}
                                  onChange={e =>
                                    setEstimateSendToName(e.target.value)
                                  }
                                  placeholder="Customer name"
                                  size="sm"
                                />

                                <Input
                                  value={estimateSendToEmail}
                                  isReadOnly={estimateSigned}
                                  onChange={e =>
                                    setEstimateSendToEmail(e.target.value)
                                  }
                                  placeholder="Customer email"
                                  size="sm"
                                />
                              </HStack>
                              <Input
                                value={emailSubject}
                                isReadOnly={estimateSigned}
                                onChange={e => setEmailSubject(e.target.value)}
                                placeholder="Email subject"
                                size="sm"
                              />
                              <Textarea
                                value={emailBody}
                                isReadOnly={estimateSigned}
                                onChange={e => setEmailBody(e.target.value)}
                                placeholder="Email message"
                                size="sm"
                                rows={8}
                                resize="vertical"
                              />
                              {api && (
                                <EmailPreview
                                  api={api}
                                  sendToName={estimateSendToName}
                                  emailBody={emailBody}
                                />
                              )}

                              <HStack justify="center" spacing={3}>
                                <Button
                                  colorScheme="green"
                                  size="sm"
                                  onClick={handleSendToCustomer}
                                  isLoading={sendPdfLoading}
                                  isDisabled={
                                    !isCustomerEmailValid ||
                                    !isCustomerNameFilled ||
                                    !emailSubject?.trim() ||
                                    !emailBody?.trim() ||
                                    estimateSigned ||
                                    !canSendEstimate
                                  }
                                >
                                  <Text fontSize="xs">Send For Signature</Text>
                                </Button>
                                {estimateSigned && (
                                  <SignedBadge dateValue={estimateSignedDate} />
                                )}
                              </HStack>

                              {!estimateSigned &&
                                (!isCustomerNameFilled ||
                                  !isCustomerEmailValid) && (
                                  <Text
                                    fontSize="xs"
                                    color="red.500"
                                    textAlign="center"
                                  >
                                    Enter a customer name and a valid email
                                    above before sending.
                                  </Text>
                                )}

                              {!estimateSigned && !hasAnyOptionFilled && (
                                <Text
                                  fontSize="xs"
                                  color="red.500"
                                  textAlign="center"
                                >
                                  Enter at least one pricing option above before
                                  sending.
                                </Text>
                              )}
                              {!estimateSigned &&
                                hasAnyOptionFilled &&
                                hasOptionMissingPrice && (
                                  <Text
                                    fontSize="xs"
                                    color="red.500"
                                    textAlign="center"
                                  >
                                    Enter a price for every option that's filled
                                    in above.
                                  </Text>
                                )}
                              {!estimateSigned && hasUpgradeMissingPrice && (
                                <Text
                                  fontSize="xs"
                                  color="red.500"
                                  textAlign="center"
                                >
                                  Enter a price for every upgrade that's filled
                                  in above.
                                </Text>
                              )}

                              {(lastSentDate ||
                                accessedDate ||
                                viewedDate ||
                                rejectedDate) && (
                                <>
                                  <Divider />
                                  <VStack align="stretch" spacing={1}>
                                    <HStack justify="center" spacing={1}>
                                      <Text
                                        fontSize="xs"
                                        fontWeight="semibold"
                                        color="gray.600"
                                      >
                                        Activity
                                      </Text>
                                      <IconButton
                                        aria-label="Refresh customer activity"
                                        title="Refresh"
                                        icon={<RepeatIcon />}
                                        size="xs"
                                        variant="ghost"
                                        isLoading={statusRefreshing}
                                        onClick={refreshEstimateStatus}
                                      />
                                    </HStack>
                                    {lastSentDate && (
                                      <Text fontSize="xs" color="gray.700">
                                        Sent:{' '}
                                        {new Date(
                                          lastSentDate
                                        ).toLocaleDateString(undefined, {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                        })}{' '}
                                        at{' '}
                                        {new Date(
                                          lastSentDate
                                        ).toLocaleTimeString(undefined, {
                                          hour: 'numeric',
                                          minute: '2-digit',
                                        })}
                                        {emailStatus === 'delivered' && (
                                          <Text as="span" color="green.600">
                                            {' '}
                                            · Delivered
                                          </Text>
                                        )}
                                        {emailStatus === 'failed' && (
                                          <Text as="span" color="red.500">
                                            {' '}
                                            · Bounced
                                            {emailFailReason
                                              ? `: ${emailFailReason}`
                                              : ''}
                                          </Text>
                                        )}
                                        {linkExpired && !estimateSigned && (
                                          <Text as="span" color="red.500">
                                            {' '}
                                            · Link expired
                                          </Text>
                                        )}
                                      </Text>
                                    )}
                                    {accessedDate && (
                                      <Text fontSize="xs" color="gray.700">
                                        Clicked link:{' '}
                                        {new Date(
                                          accessedDate
                                        ).toLocaleDateString(undefined, {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                        })}{' '}
                                        at{' '}
                                        {new Date(
                                          accessedDate
                                        ).toLocaleTimeString(undefined, {
                                          hour: 'numeric',
                                          minute: '2-digit',
                                        })}
                                      </Text>
                                    )}
                                    {accessedDate &&
                                      !viewedDate &&
                                      !rejectedDate &&
                                      !estimateSigned && (
                                        <>
                                          <Text
                                            fontSize="xs"
                                            fontWeight="semibold"
                                            color="red.500"
                                          >
                                            The customer clicked on the link but
                                            did not view the estimate.
                                          </Text>
                                          <Text
                                            fontSize="xs"
                                            fontWeight="semibold"
                                            color="red.500"
                                          >
                                            There may have been a technical
                                            error or they may have declined the
                                            ERSD.
                                          </Text>
                                        </>
                                      )}
                                    {viewedDate && (
                                      <Text fontSize="xs" color="gray.700">
                                        Viewed estimate:{' '}
                                        {new Date(
                                          viewedDate
                                        ).toLocaleDateString(undefined, {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                        })}{' '}
                                        at{' '}
                                        {new Date(
                                          viewedDate
                                        ).toLocaleTimeString(undefined, {
                                          hour: 'numeric',
                                          minute: '2-digit',
                                        })}
                                      </Text>
                                    )}
                                    {rejectedDate && (
                                      <Text fontSize="xs" color="gray.700">
                                        Rejected:{' '}
                                        {new Date(
                                          rejectedDate
                                        ).toLocaleDateString(undefined, {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                        })}{' '}
                                        at{' '}
                                        {new Date(
                                          rejectedDate
                                        ).toLocaleTimeString(undefined, {
                                          hour: 'numeric',
                                          minute: '2-digit',
                                        })}{' '}
                                        for {estimateRejectedReason}
                                      </Text>
                                    )}
                                  </VStack>
                                </>
                              )}
                            </SectionCard>
                          </VStack>
                        </TabPanel>
                        {estimateSigned && (
                          <TabPanel>
                            <VStack>
                              <HStack spacing={1} alignSelf="center">
                                <Text color="green.500">✅</Text>
                                <Text
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  color="green.600"
                                >
                                  Signed{' '}
                                  {new Date(
                                    estimateSignedDate
                                  ).toLocaleDateString(undefined, {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })}{' '}
                                  at{' '}
                                  {new Date(
                                    estimateSignedDate
                                  ).toLocaleTimeString(undefined, {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                  })}
                                </Text>
                              </HStack>
                              {signedPdfBlobUrl && (
                                <Box
                                  border="2px solid black"
                                  display="inline-block"
                                  maxW="100%"
                                  overflowX="auto"
                                >
                                  {getSignedPdfLoading ? (
                                    <Center>
                                      <VStack>
                                        <Spinner
                                          thickness="6px"
                                          speed="1.25s"
                                          size="lg"
                                          emptyColor="gray.200"
                                          color="black.500"
                                        />
                                        <Text>Loading...</Text>
                                      </VStack>
                                    </Center>
                                  ) : (
                                    <Document file={signedPdfBlobUrl}>
                                      <Page
                                        pageNumber={
                                          estimateSignaturePage ||
                                          estimateStartPage ||
                                          1
                                        }
                                        width={
                                          typeof window !== 'undefined'
                                            ? Math.min(
                                                600,
                                                window.innerWidth - 80
                                              )
                                            : 600
                                        }
                                      />
                                    </Document>
                                  )}
                                </Box>
                              )}
                              <Button
                                marginY="10px"
                                leftIcon={<FaDownload />}
                                colorScheme="blue"
                                size="sm"
                                onClick={downloadSignedPdf}
                                isLoading={signedPdfLoading}
                              >
                                <Text fontSize="xs">Download Signed PDF</Text>
                              </Button>
                            </VStack>
                          </TabPanel>
                        )}
                        {true && (
                          <TabPanel>
                            <VStack align="center" w="100%" spacing={8}>
                              {['standard', 'better', 'best'].map(
                                (tier, tierIndex) => {
                                  const data = {
                                    standard: breakdownData,
                                    better: breakdownDataBetter,
                                    best: breakdownDataBest,
                                  }[tier];
                                  return (
                                    <Box key={tier} w="100%">
                                      {tierIndex > 0 && <Divider mb={8} />}
                                      <Text
                                        fontWeight="bold"
                                        fontSize="sm"
                                        textAlign="center"
                                        mb={3}
                                      >
                                        {TIER_LABELS[tier]}
                                      </Text>
                                      <VStack align="center" w="100%">
                                        <Button
                                          marginY="10px"
                                          leftIcon={<FaDownload />}
                                          colorScheme="green"
                                          size="sm"
                                          onClick={() =>
                                            downloadBreakdown(tier)
                                          }
                                          isLoading={summaryLoading}
                                        >
                                          <Text fontSize="xs">
                                            Download Breakdown (
                                            {TIER_LABELS[tier]})
                                          </Text>
                                        </Button>
                                        <TableContainer>
                                          <Table
                                            variant="striped"
                                            size="sm"
                                            marginBottom="20px"
                                            mx="auto"
                                          >
                                            <Thead>
                                              <Tr>
                                                {data[0]?.map(
                                                  (header, index) => (
                                                    <Th key={index}>
                                                      {header}
                                                    </Th>
                                                  )
                                                )}
                                              </Tr>
                                            </Thead>
                                            <Tbody>
                                              {data
                                                .slice(1)
                                                ?.map((row, rowIndex) => (
                                                  <Tr key={rowIndex}>
                                                    {row?.map(
                                                      (cell, cellIndex) => (
                                                        <Td key={cellIndex}>
                                                          {boldList.includes(
                                                            cell
                                                          ) ? (
                                                            <Text as="b">
                                                              {cell}
                                                            </Text>
                                                          ) : (
                                                            <>{cell}</>
                                                          )}
                                                        </Td>
                                                      )
                                                    )}
                                                  </Tr>
                                                ))}
                                            </Tbody>
                                          </Table>
                                        </TableContainer>
                                      </VStack>
                                    </Box>
                                  );
                                }
                              )}
                            </VStack>
                          </TabPanel>
                        )}
                        {true && (
                          <TabPanel>
                            <VStack align="center" w="100%" spacing={8}>
                              {['standard', 'better', 'best'].map(
                                (tier, tierIndex) => (
                                  <Box key={tier} w="100%">
                                    {tierIndex > 0 && <Divider mb={8} />}
                                    <Text
                                      fontWeight="bold"
                                      fontSize="sm"
                                      textAlign="center"
                                      mb={3}
                                    >
                                      {TIER_LABELS[tier]}
                                    </Text>
                                    <VStack align="center" w="100%">
                                      <Button
                                        marginY="10px"
                                        leftIcon={<FaDownload />}
                                        colorScheme="green"
                                        size="sm"
                                        onClick={() => downloadCrewBill(tier)}
                                        isLoading={crewBillLoading}
                                      >
                                        <Text fontSize="xs">
                                          Download Crew Bill (
                                          {TIER_LABELS[tier]})
                                        </Text>
                                      </Button>
                                      <TableContainer>
                                        <DataTable
                                          data={
                                            {
                                              standard: crewBillData,
                                              better: crewBillDataBetter,
                                              best: crewBillDataBest,
                                            }[tier]
                                          }
                                          size={isLargerThan1200 ? 'sm' : 'xs'}
                                        />
                                      </TableContainer>
                                    </VStack>
                                  </Box>
                                )
                              )}
                            </VStack>
                          </TabPanel>
                        )}
                        {true && (
                          <TabPanel>
                            <VStack align="center" w="100%" spacing={8}>
                              {['standard', 'better', 'best'].map(
                                (tier, tierIndex) => (
                                  <Box key={tier} w="100%">
                                    {tierIndex > 0 && <Divider mb={8} />}
                                    <Text
                                      fontWeight="bold"
                                      fontSize="sm"
                                      textAlign="center"
                                      mb={3}
                                    >
                                      {TIER_LABELS[tier]}
                                    </Text>
                                    <VStack align="center" w="100%">
                                      <Button
                                        marginY="10px"
                                        leftIcon={<FaDownload />}
                                        colorScheme="green"
                                        size="sm"
                                        onClick={() =>
                                          downloadMaterialList(tier)
                                        }
                                        isLoading={materialListLoading}
                                      >
                                        <Text fontSize="xs">
                                          Download Material List (
                                          {TIER_LABELS[tier]})
                                        </Text>
                                      </Button>
                                      <TableContainer>
                                        <DataTable
                                          data={
                                            {
                                              standard: materialListData,
                                              better: materialListDataBetter,
                                              best: materialListDataBest,
                                            }[tier]
                                          }
                                          size={isLargerThan1200 ? 'sm' : 'xs'}
                                        />
                                      </TableContainer>
                                    </VStack>
                                  </Box>
                                )
                              )}
                            </VStack>
                          </TabPanel>
                        )}
                        <TabPanel>
                          <TableContainer>
                            <Button
                              marginY="10px"
                              leftIcon={<FaDownload />}
                              colorScheme="green"
                              size="sm"
                              onClick={downloadItemizedList}
                              isLoading={itemizedListLoading}
                            >
                              <Text fontSize="xs">Download Itemized</Text>
                            </Button>
                            <Table variant="striped" size="sm">
                              <Thead>
                                <Tr>
                                  {itemizedData[0]?.map((header, index) => (
                                    <Th key={index}>{header}</Th>
                                  ))}
                                </Tr>
                              </Thead>
                              <Tbody>
                                {itemizedData
                                  ?.slice(1)
                                  ?.map((row, rowIndex) => (
                                    <Tr key={rowIndex}>
                                      {row?.map((cell, cellIndex) => (
                                        <Td key={cellIndex}>{cell} </Td>
                                      ))}
                                    </Tr>
                                  ))}
                              </Tbody>
                            </Table>
                            <HStack py="5">
                              <Text fontSize="sm">Description</Text>
                              <Input
                                size="sm"
                                value={customItemDescription}
                                onChange={event => {
                                  setCustomItemDescription(event.target.value);
                                }}
                              ></Input>
                              <Text fontSize="sm">Qty</Text>
                              <NumberInput
                                size="sm"
                                maxWidth="120px"
                                step={1}
                                min={0}
                                value={customItemQuantity}
                                onChange={value => {
                                  setCustomItemQuantity(value);
                                }}
                              >
                                <NumberInputField />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                              <Text fontSize="sm">Rate</Text>
                              <NumberInput
                                size="sm"
                                maxWidth="120px"
                                step={1}
                                min={0}
                                value={customItemRate}
                                onChange={value => {
                                  setCustomItemRate(value);
                                }}
                              >
                                <NumberInputField />
                                <NumberInputStepper>
                                  <NumberIncrementStepper />
                                  <NumberDecrementStepper />
                                </NumberInputStepper>
                              </NumberInput>
                              <IconButton
                                colorScheme="blue"
                                size="sm"
                                aria-label="Add"
                                onClick={() => {
                                  handleAddCustomItem();
                                }}
                                icon={<AddIcon />}
                              />
                            </HStack>
                            <Button
                              rightIcon={<DeleteIcon />}
                              colorScheme="red"
                              size="sm"
                              onClick={handleDeleteCustomItems}
                            >
                              <Text fontSize="xs">Clear</Text>
                            </Button>
                          </TableContainer>
                        </TabPanel>
                        <TabPanel>
                          <VStack>
                            <Button
                              marginY="10px"
                              leftIcon={<FaDownload />}
                              colorScheme="blue"
                              size="sm"
                              onClick={downloadPdf}
                              isLoading={pdfLoading}
                            >
                              <Text fontSize="xs">Download Itemized PDF</Text>
                            </Button>

                            {pdfBlobUrl && (
                              <Box
                                border="2px solid black"
                                display="inline-block"
                                maxW="100%"
                                overflowX="auto"
                              >
                                {getItemizedPdfLoading ? (
                                  <Center>
                                    <VStack>
                                      <Spinner
                                        thickness="6px"
                                        speed="1.25s"
                                        size="lg"
                                        emptyColor="gray.200"
                                        color="black.500"
                                      />
                                      <Text>Calculating...</Text>
                                    </VStack>
                                  </Center>
                                ) : (
                                  <Document file={pdfBlobUrl}>
                                    <Page
                                      pageNumber={1}
                                      width={
                                        typeof window !== 'undefined'
                                          ? Math.min(
                                              600,
                                              window.innerWidth - 80
                                            )
                                          : 600
                                      }
                                    />
                                  </Document>
                                )}
                              </Box>
                            )}
                          </VStack>
                        </TabPanel>
                      </TabPanels>
                    </Tabs>
                  </VStack>
                </Center>
              )}
            </ModalBody>
            <ModalFooter>
              {/* <Box
              position="fixed"
              bottom="10px" // Adjust the positioning as per your requirement
              right="10px" // Adjust the positioning as per your requirement
              zIndex="999" // Set a higher z-index to ensure the button stays on top of other content
              backgroundColor="gray.100" // Set the background color for the container
              padding="1rem" // Add padding to create some space around the button
              borderRadius="md" // Add border radius for rounded corners
            > */}
              <Button onClick={onClose}>Close</Button>
              {SHOW_SEND_TO_ADMIN && (
                <Button colorScheme="blue" onClick={handleSend} ml={3}>
                  Send To Admin
                </Button>
              )}
              {/* </Box> */}
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      </Modal>
      <AlertDialog
        isOpen={isSendOpen}
        leastDestructiveRef={cancelSendRef}
        onClose={onSendClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Send Estimate
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="flex-start" spacing="20px">
                <Text>Send current estimate to your administrator?</Text>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelSendRef} onClick={onSendClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={onSendConfirm} ml={3}>
                {isSendLoading ? (
                  <Spinner
                    thickness="4px"
                    speed="1.25s"
                    size="md"
                    emptyColor="gray.200"
                    color="gray.800"
                  />
                ) : (
                  <Text>Send To Admin</Text>
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <AlertDialog
        isOpen={isSendToCustomerOpen}
        leastDestructiveRef={cancelSendToCustomerRef}
        onClose={onSendToCustomerClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Send Estimate
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={4} textAlign="left">
                <Box
                  px={4}
                  py={3}
                  bg="gray.50"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  {estimateSendToName?.trim() && (
                    <Text fontWeight="semibold">{estimateSendToName}</Text>
                  )}
                  <Text fontSize="sm" color="gray.700" wordBreak="break-all">
                    {estimateSendToEmail}
                  </Text>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    The signature link works for 90 days.
                  </Text>
                </Box>

                {zeroPricedOptions.map((name, index) => (
                  <Alert
                    key={`${index}-${name}`}
                    status="warning"
                    borderRadius="md"
                    fontSize="sm"
                  >
                    <AlertIcon />
                    {name} has a price of $0.00.
                  </Alert>
                ))}

                {lastSentDate && (
                  <Text fontSize="xs" color="gray.500" textAlign="right">
                    Sending cancels the link sent on{' '}
                    {new Date(lastSentDate).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                    .
                  </Text>
                )}
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelSendToCustomerRef}
                onClick={onSendToCustomerClose}
              >
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={onSendToCustomerConfirm}
                ml={3}
              >
                {isSendToCustomerLoading ? (
                  <Spinner
                    thickness="4px"
                    speed="1.25s"
                    size="md"
                    emptyColor="gray.200"
                    color="gray.800"
                  />
                ) : (
                  <Text>Send For Signature</Text>
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default Calculate;
